import {
  ADD_ORDERS,
  ADD_REFUND_REQUEST_TO_ORDER,
  API_CALL,
  REMOVE_ORDER,
  SET_GUEST_SPECIAL_REQUESTS,
  TOUR_TRAVELLER_DETAILS_RESET_SUBMISSION,
  TOUR_TRAVELLER_DETAILS_UPDATE_SUBMITTED,
  UPDATE_ORDER,
} from './actionConstants'

import {
  CANCEL_ORDER_GIFTING,
  CREATE_DATES_REQUEST,
  FETCH_ORDER,
  FETCH_ORDERS_DATES_REQUESTS,
  FETCH_ORDERS_FOR_CURRENT_USER,
  FETCH_PAYMENT_METHOD_STRIPE,
  FETCH_TOUR_ORDER_TRAVELLER_DETAILS,
  SUBMIT_ORDER_TRAVELLERS,
  SAVE_DRAFT_ORDER_TRAVELLERS,
  MARK_EXPERIENCE_BOOKING_DETAILS_AS_REDEEMED,
  FETCH_ORDER_ITEM_FLIGHT_DETAILS,
  FETCH_ORDER_ITEM_CRUISE_DETAILS,
  FETCH_ORDER_ACCOMMODATION_BOOKINGS,
  FETCH_FLIGHT_ORDER_PNRS,
  FETCH_ORDER_BY_SUBSCRIPTION,
  FETCH_ROOM_REFUND_INFO,
  FETCH_GROUP_PAYMENT_REFUNDABLE_SOURCES,
  UPDATE_DATES_REQUEST,
  FETCH_ORDER_CANCELLATION_CONFIGS,
} from './apiActionConstants'
import {
  cancelOrderGiftingRequest,
  getBrandOrders,
  getOrders,
  getOrderDatesRequest,
  getPaymentMethodFromStripe,
  getReservationDetailsForItems,
  markExperienceItemAsRedeemed,
  postDatesRequest,
  getOrderBySubscriptionId,
  getPossibleRoomRefundTotal,
  updateDatesRequest,
  updateGuestSpecialRequests,
  getOrderItemsCancellationConfigs,
  getFullOrder,
  getViewableOrder,
  getFlightDetailsWithDedup,
  getCruiseDetails,
  type createDatesRequestParams,
  type SpecialRequestStatus,
} from 'api/order'
import { getAccommodationBookingDetails } from 'api/accommodationBooking'
import { getOrderById } from 'api/orderLookup'
import type {
  PostOrderTravellerDetailsParams,
  PostOrderTravellerDetailsPayload,
} from 'api/traveller'
import {
  reqGetTourTravellerDetailsFromOrderService,
  updateOrderTravellerDetailsToOrderService,
} from 'api/traveller'
import { arrayToObject } from 'lib/array/arrayUtils'
import { getPaymentDetailsForOrders, getRefundableSources } from 'api/payment'
import type { AppAction } from './types/ActionTypes'
import type { ApiActionResult } from 'middlewares/apiMiddleware'
import { TOUR_ORDER_TRAVELLER_DETAILS_TTL } from 'constants/tours'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { OrderItemType } from 'constants/order'
import { areOrdersBeingFetched } from 'lib/order/orderUtils'
import { isAgentHubTravellerOrder } from 'lib/order/agentHubTravellerOrder'
import pollOrderService from 'lib/polling/pollOrderService'
import getObjectKey from 'lib/object/getObjectKey'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import config from 'constants/config'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import getDatesRequestErrorMessage from 'lib/order/getDatesRequestErrorMessage'

export function fetchRoomRefundInfo(
  orderId: string,
  itemId: string,
  roomId?: string,
  options?: { force?: boolean },
): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const key = getObjectKey({ orderId, itemId, roomId })
    const existing = state.orders.itemRefundInfo[key]
    if (!options?.force && existing) {
      // already fetched it/processing it
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_ROOM_REFUND_INFO,
      request: () => getPossibleRoomRefundTotal(orderId, itemId, roomId),
      key,
    })
  }
}

export function fetchGroupPaymentRefundableSources(orderId: string, options?: { force?: boolean }): AppAction {
  return (dispatch, getState) => {
    const record = getState().orders.groupPaymentRefundableSources[orderId]
    if (record?.fetching || (!options?.force && (record?.info || record?.error))) return

    dispatch({
      type: API_CALL,
      api: FETCH_GROUP_PAYMENT_REFUNDABLE_SOURCES,
      request: () => getRefundableSources(orderId),
      orderId,
    })
  }
}

export function fetchOrderBySubscriptionId(subscriptionId: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const orderId = state.orders.ordersBySubscriptionId[subscriptionId]

    if (state.orders.orders[orderId] || state.orders.ordersFetching[subscriptionId]) {
      // already have the order or are fetching it, don't try again
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_ORDER_BY_SUBSCRIPTION,
      request: () => getOrderBySubscriptionId(subscriptionId),
      subscriptionId,
    })
  }
}
interface FetchOrderOptions {
  force?: boolean
}

export function fetchOrder(orderId: string, options?: FetchOrderOptions): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const existingOrder = state.orders.orders[orderId]
    const existingRestrictedOrder = state.orders.agentHubTravellerOrders[orderId]

    if (
      // don't refetch if we're already fetching it (unless forced)
      (!options?.force && state.orders.ordersFetching[orderId]) ||
      (!options?.force && existingRestrictedOrder?.type === 'full') ||
      // we already have the full commercial version
      (existingOrder?.type === 'full' && !options?.force) ||
      // it errored (unless forced — e.g. confirmation refresh after bedbank amendment poll)
      !options?.force && !!state.orders.orderErrors[orderId]
    ) {
      return
    }

    const isLoyaltyEnabled = getIsLoyaltyEnabled(state)
    dispatch({
      type: API_CALL,
      api: FETCH_ORDER,
      request: () => config.AGENT_HUB_TRAVELLER_TRIP_ACCESS_ENABLED
        ? getViewableOrder(orderId, { isLoyaltyEnabled })
        : getFullOrder(orderId, { isLoyaltyEnabled }),
      orderId,
    })
  }
}

export function fetchBasicOrder(orderId: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    if (
      state.orders.ordersFetching[orderId] ||
      state.orders.orders[orderId] ||
      state.orders.orderErrors[orderId]
    ) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_ORDER,
      request: () => getOrderById(orderId),
      orderId,
    })
  }
}

export function updateOrder(updatedOrder: App.Order): AppAction {
  return {
    type: UPDATE_ORDER,
    order: updatedOrder,
    orderId: updatedOrder.id,
  }
}

/** Apply polled bedbank item statuses immediately, then refresh the full order. */
export function refreshOrderAfterBedbankAmendment(orderId: string, polledOrder: App.Order): AppAction {
  return (dispatch, getState) => {
    const currentOrder = getState().orders.orders[orderId]
    if (!currentOrder) {
      return
    }
    dispatch(updateOrder({
      ...currentOrder,
      bedbankItems: polledOrder.bedbankItems,
    }))
    dispatch(fetchOrder(orderId, { force: true }))
  }
}

export function removeOrder(orderId: string): AppAction {
  return {
    type: REMOVE_ORDER,
    orderId,
  }
}

interface TourOrderTravellerDetailsFetcherOptions {
  forceFetch?: boolean
}
export function fetchTourOrderTravellerDetails(tourOrderId: string, options?: TourOrderTravellerDetailsFetcherOptions): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const orderDetails = state.orders.orders[tourOrderId]
    const existingOrderTravellerDetails = state.orders.tourOrdersTravellerDetails[tourOrderId]
    const shouldFetch = !!options?.forceFetch || !existingOrderTravellerDetails
    const hasFetchExpired = existingOrderTravellerDetails?.status === 'fetched' &&
      (existingOrderTravellerDetails.fetchedAtTimeStamp ?? 0) + TOUR_ORDER_TRAVELLER_DETAILS_TTL < new Date().getTime()
    const tourItemsLength = orderDetails?.tourItems.length ?? 0

    if (orderDetails && tourItemsLength > 0 && (shouldFetch || hasFetchExpired)) {
      dispatch({
        type: API_CALL,
        api: FETCH_TOUR_ORDER_TRAVELLER_DETAILS,
        request: () => reqGetTourTravellerDetailsFromOrderService({
          bookingNumber: orderDetails.tourItems[0].bookingId,
          orderId: tourOrderId,
        }),
        tourOrderId,
      })
    }
  }
}

export function updateTourTravellerDetailsSubmitted(tourOrderId: string, travellerDetails: App.TourOrderTravellerDetails): AppAction {
  return {
    type: TOUR_TRAVELLER_DETAILS_UPDATE_SUBMITTED,
    travellerDetails,
    tourOrderId,
  }
}

export function resetTourTravellerDetailsSubmission(): AppAction {
  return {
    type: TOUR_TRAVELLER_DETAILS_RESET_SUBMISSION,
  }
}

export function submitTourTravellersDetails(
  payload: PostOrderTravellerDetailsPayload,
  params?: PostOrderTravellerDetailsParams,
): AppAction {
  return {
    type: API_CALL,
    api: params?.isDraft ? SAVE_DRAFT_ORDER_TRAVELLERS : SUBMIT_ORDER_TRAVELLERS,
    request: () => updateOrderTravellerDetailsToOrderService(payload, params),
  }
}

interface FetchOrderParams {
  status?: 'upcoming'
  sort?: 'asc' | 'desc'
  itemType?: OrderItemType
  limit?: number
  includeSharedOrders?: boolean
}

const StreamOrderPageSize = 20
export async function* fetchAndStreamOrders(
  memberId: string,
  currentRegionCode: string,
  params: FetchOrderParams = {},
) {
  let end = false
  let page = 1
  while (!end) {
    const results = await getOrders(memberId, currentRegionCode, {
      ...params,
      sort: params.sort ?? 'desc',
      page,
      pageSize: StreamOrderPageSize,
    })
    yield results.orders

    end = page * StreamOrderPageSize >= results.total
    page++
  }

  const brandOrders = await getBrandOrders(memberId)
  if (brandOrders.length > 0) {
    yield brandOrders
  }
}

export function fetchOrders(params: FetchOrderParams = {}): AppAction {
  return (dispatch, getState) => {
    let state = getState()
    const memberId = state.auth.account.memberId

    const isAlreadyFetching = areOrdersBeingFetched(state, params.status)
    if (isAlreadyFetching || !memberId) {
      // already happening, don't start it again
      return
    }

    dispatch({
      api: FETCH_ORDERS_FOR_CURRENT_USER,
      type: API_CALL,
      request: async() => {
        state = getState()

        const opexBookingAuthorityFeatureFlag = state.system.features.flags[OptimizelyFeatureFlags.opexBookingAuthority]
        const newParams = {
          ...params,
          includeSharedOrders: Boolean(opexBookingAuthorityFeatureFlag) || config.AGENT_HUB_TRAVELLER_TRIP_ACCESS_ENABLED,
        }

        for await (const orders of fetchAndStreamOrders(memberId, state.geo.currentRegionCode, newParams)) {
          // while waiting for orders to stream, other things may have happened so get the latest state now
          // pre-emptive dispatch of order data without their additioanl meta data
          // this improves preceived load times
          // An order fetch can complete while this stream waits for its next page;
          // refresh state here before deciding which streamed orders are still genuinely missing.
          state = getState()
          const missingOrders = orders.filter(order =>
            !state.orders.orders[order.id] &&
            !state.orders.agentHubTravellerOrders[order.id] &&
            !state.orders.ordersFetching[order.id],
          )
          if (missingOrders.length) {
            dispatch({ type: ADD_ORDERS, orders: missingOrders })

            const commercialOrders = missingOrders.filter((order): order is App.Order => !isAgentHubTravellerOrder(order))
            if (!commercialOrders.length) {
              continue
            }

            const clonedOrders = commercialOrders.map(order => ({ ...order }))

            // do this all non-blocking so we can continue streaming in orders
            // do not fail on a failure - it just won't update that portion
            const orderPromises = [
              getPaymentDetailsForOrders(clonedOrders.map(order => order.id)).then(paymentDetails => {
                clonedOrders.forEach(order => {
                  const orderDetails = paymentDetails[order.id]
                  if (orderDetails) {
                    order.payments = orderDetails.payments.filter(payment => payment.type !== 'manual') ?? []
                    order.depositDetails = orderDetails.depositDetails
                    order.instalmentDetails = orderDetails.instalmentDetails
                    order.paymentScheduleDetails = orderDetails.paymentScheduleDetails
                    order.groupPaymentDetails = orderDetails.groupPaymentDetails
                    order.reserveForZeroDetails = orderDetails.deferredPaymentDetails
                  }
                })
              }),
              ...clonedOrders.map(order => getReservationDetailsForItems(
                order.items.filter(item => item.reservationMade),
                order.id).then(reservations => {
                const reservationsById = arrayToObject(reservations, res => res.itemId)
                order.items = order.items.map(item => {
                  if (reservationsById[item.id]) {
                    return {
                      ...item,
                      reservation: reservationsById[item.id],
                    }
                  }
                  return item
                })
              })),
            ]

            Promise.allSettled(orderPromises).then(() => {
              dispatch({ type: ADD_ORDERS, orders: clonedOrders })
            })
          }
        }

        // For this action we're adding the orders to the state via ADD_ORDERS, so just return an empty array here
        return []
      },
      key: params.status || 'all',
    })
  }
}

/**
 * Some upcoming orders are upsellable based on the item types that it contains.
 * This action will fetch up to 5 upcoming orders for EACH upsellable item types.
 * Right now, only LE hotel item is upsellable, so it will return up to 5 orders.
 */
export function fetchUpsellableOrders(): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const memberId = state.auth.account.memberId

    if (!memberId) {
      return
    }

    dispatch({
      api: FETCH_ORDERS_FOR_CURRENT_USER,
      type: API_CALL,
      request: () => getOrders(memberId, state.geo.currentRegionCode, {
        page: 1,
        pageSize: 5,
        itemType: OrderItemType.ACCOMMODATION,
        sort: 'asc',
      }).then(result => result.orders),
      key: 'upsellable',
    })
  }
}

export function createDatesRequest(body: createDatesRequestParams, onResolve: () => void): AppAction {
  return {
    type: API_CALL,
    api: CREATE_DATES_REQUEST,
    request: () => {
      const intl = getCurrentIntl()
      return postDatesRequest(body)
        .then(() => {
          showSnackbar(
            intl.formatMessage({
              id: '6uqyOi4_kk02Y9p4JhlN',
              defaultMessage: 'You will receive an email shortly confirming your request',
              description: 'Snackbar message after successfully sending a date request',
            }),
            'success',
            {
              heading: intl.formatMessage({
                id: 'oZZWdm93ExOSCjOoblQA',
                defaultMessage: 'Your request has been successfully sent',
                description: 'Snackbar heading after successfully sending a date request',
              }),
            },
          )
          onResolve()
        })
        .catch(e => {
          const fallbackErrorMessage = typeof e?.message === 'string' && e.message
            ? e.message
            : intl.formatMessage({
                id: 'xHI1oSmoS83Qdh0rUxKf',
                defaultMessage: 'We have an error in our system. Try again later.',
                description: 'Fallback snackbar message when sending a date request fails without a structured backend error message.',
              })

          showSnackbar(
            getDatesRequestErrorMessage(e, fallbackErrorMessage, intl),
            'critical',
            {
              heading: intl.formatMessage({
                id: 'IInzLABMsiMu9tng6C9h',
                defaultMessage: 'Your date request has not been sent.',
                description: 'Snackbar heading shown when a booking date request fails to send',
              }),
            },
          )
          onResolve()
        })
    },
    body,
  }
}

export function fetchOrderDatesRequests(orderId: string): AppAction {
  return {
    type: API_CALL,
    api: FETCH_ORDERS_DATES_REQUESTS,
    request: () => getOrderDatesRequest(orderId),
    orderId,
  }
}

export function updateOrderDatesRequest(datesRequest: App.DatesRequest, status: string): AppAction {
  return {
    type: API_CALL,
    api: UPDATE_DATES_REQUEST,
    request: () => updateDatesRequest(datesRequest.id, status),
    datesRequest,
  }
}

export function cancelOrderGifting(orderId: string): AppAction {
  return (dispatch) => {
    dispatch({
      type: API_CALL,
      api: CANCEL_ORDER_GIFTING,
      request: () => cancelOrderGiftingRequest(orderId)
        .then(() => dispatch(fetchOrder(orderId, { force: true }))),
    })
  }
}

export function fetchOrderPaymentMethod(paymentMethodId: string, currency: string): AppAction {
  return {
    type: API_CALL,
    api: FETCH_PAYMENT_METHOD_STRIPE,
    request: () => getPaymentMethodFromStripe(paymentMethodId, currency),
  }
}

export function markExperienceOrderItemAsRedeemed(experienceItemId: string): AppAction {
  return {
    type: API_CALL,
    api: MARK_EXPERIENCE_BOOKING_DETAILS_AS_REDEEMED,
    request: () => {
      const intl = getCurrentIntl()
      return new Promise<{ markedAsRedeemed: boolean }>((resolve, reject) => {
        // easiest way to implement an 'undo' is to not do the action
        // until 'undo' is no longer available
        // so only actually mark the item as redeemed once the notification disappears
        const timeout = window.setTimeout(() => {
          markExperienceItemAsRedeemed(experienceItemId)
            .then(() => resolve({ markedAsRedeemed: true }))
            .catch(reject)
        }, 8000)

        showSnackbar(
          intl.formatMessage({
            id: 'IuJ8TkO1X0Csk5QEv_Bj',
            defaultMessage: 'Your voucher has been marked as redeemed',
            description: 'Snackbar message after marking a voucher as redeemed',
          }),
          'success',
          {
            heading: intl.formatMessage({
              id: 'RG5370B6lZBYs6oIOfiu',
              defaultMessage: 'Successfully redeemed',
              description: 'Snackbar heading after marking a voucher as redeemed',
            }),
            action: {
              label: intl.formatMessage({
                id: 'yEgPcFbhnhZOcc6uOnnL',
                defaultMessage: 'Undo',
                description: 'Label for undo action button on order cancellation snackbar',
              }),
              onAction: () => {
                // prevent the timeout from firing
                window.clearTimeout(timeout)
                resolve({ markedAsRedeemed: false })
              },
            },
          },
        )
      })
    },
    experienceItemId,
  }
}

export function fetchOrderItemFlightDetails(orderId: string, itemId: string, skipCache: boolean = false): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    if (state.orders.flightDetails[itemId] && !skipCache) {
      // already fetched/are fetching, don't try again
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_ORDER_ITEM_FLIGHT_DETAILS,
      request: () => getFlightDetailsWithDedup({ orderId, itemId, skipCache }),
      key: itemId,
    })
  }
}

export function fetchOrderItemCruiseDetails(orderId: string, itemId: string, force = false): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const cruiseDetailsState = state.orders.cruiseDetails[itemId]
    if (cruiseDetailsState?.fetching || (!force && cruiseDetailsState?.cruise)) {
      // already fetching or already have data, don't try again
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_ORDER_ITEM_CRUISE_DETAILS,
      request: () => getCruiseDetails({ orderId, itemId }, state.geo.currentRegionCode),
      key: itemId,
    })
  }
}

/**
 * Loads the full svc-accommodation booking detail for every `accommodationV2` item on an order.
 *
 * Keyed by order id rather than item id: one request returns all of the order's bookings, and a
 * multi-occupancy stay is several bookings on the same order.
 */
export function fetchOrderAccommodationBookings(orderId: string, force = false): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const bookingsState = state.orders.accommodationBookings?.[orderId]
    // Only a non-empty result counts as cached: a booking can be created after the first fetch,
    // and an empty array is truthy, which would otherwise pin the order to nothing for the life of
    // the store.
    if (bookingsState?.fetching || (!force && bookingsState?.bookings?.length)) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_ORDER_ACCOMMODATION_BOOKINGS,
      request: () => getAccommodationBookingDetails(orderId),
      orderId,
    })
  }
}

export function pollingOrderFlightsPnrId(order: App.Order): AppAction {
  return {
    type: API_CALL,
    api: FETCH_FLIGHT_ORDER_PNRS,
    request: () => pollOrderService({
      validateFunction: (order: App.Order) => {
        return order.flightItems.every((flightItem) => flightItem.pnrId && flightItem.status === 'completed') ? order : undefined
      },
      failedStatus: 'failed',
      apiCall: () => getOrderById(
        order.id,
      ),
      maxTime: 120000,
      waitTime: 2000,
    }),
  }
}

export function updateGuestSpecialRequestsPostCheckout(
  orderId: string,
  itemId: string,
  guestSpecialRequests: string,
  options?: {
    roomId?: string
    onComplete?: (result: ApiActionResult<{
      result: {
        guest_special_requests: string
        special_request_status?: SpecialRequestStatus
        room_id?: string
      }
      orderId: string
      itemId: string
      roomId?: string
    }>) => void
  },
): AppAction {
  return (dispatch) => {
    dispatch({
      type: API_CALL,
      api: SET_GUEST_SPECIAL_REQUESTS,
      request: () => updateGuestSpecialRequests(orderId, itemId, guestSpecialRequests, options?.roomId),
      onComplete: options?.onComplete,
    })
  }
}

export function addRefundRequestToOrder(refundRequest: App.RefundRequest): AppAction {
  return {
    type: ADD_REFUND_REQUEST_TO_ORDER,
    orderId: refundRequest.orderId,
    orderItemId: refundRequest.itemId,
    refundRequest,
  }
}

export function fetchOrderCancellationConfigs(orderId: string, options?: { force?: boolean }): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    if (
      state.orders.cancellationConfigsFetching[orderId] ||
      (!options?.force && state.orders.cancellationConfigs[orderId]) ||
      state.orders.cancellationConfigsErrors[orderId]
    ) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_ORDER_CANCELLATION_CONFIGS,
      request: () => getOrderItemsCancellationConfigs(orderId),
      orderId,
    })
  }
}
